import { TESTDATA_MODAL } from '@enums/overlay-internal-events.enum';
import { TEST_DATA_SYMBOLS } from '@enums/test-data-symbols.enum';
import EnvironmentService from '@services/environment.service';
import {
  toastify,
  useAnimatedClass,
  useInternalEvents,
  useTranslation,
} from '@testsigma/atoms';
import { EnvironmentValueSelector } from '@testsigma/organism';
import classNames from 'classnames';
import { useCallback, useState, useContext } from 'react';

import { TestDevelopmentContext } from '@modules/test-development/contexts/test-development-context';
import { VersionContext } from '@modules/test-development/contexts/version.context';
import { handleApiErrorMessages } from '@utils/api-handler-utils';
import { getBaseUrl } from '@api/instance';
import { createPayload } from '@modules/test-development/pages/environments/variables/utils';

import {
  TestDataDrawerHelpInfoProps,
  TestDataDrawerLayout,
} from './test-data-drawer-layout';
import { useInfiniteQuery } from 'react-query';
import { useReactQueryCaching } from '@hooks/use-react-query-caching';
import { CachingPrefix } from '@enums/caching-prefix.enum';
import { CacheOperation } from '@enums/cache-operation.enum';
export const EnvironmentDrawer = ({
  projectId,
  context,
  isInsideRestApi = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const animationClass = useAnimatedClass('reval-animation-opacity');
  const publishInternalEvents = useInternalEvents([]);
  const { getCachingTime, cacheOperationHandler } = useReactQueryCaching(
    CachingPrefix.FETCH_ENVIRONMENT_VARIABLES,
    false,
  );
  const { cacheOperationHandler: environmentHandler } = useReactQueryCaching(
    CachingPrefix.ENVIRONMENT_VARIABLES_DATA,
    false,
  );

  const helpInfoProps: TestDataDrawerHelpInfoProps = {
    description: t('modal.help.environment'),
    url: 'https://testsigma.com/docs/test-data/types/environment/',
  };

  const refetchVariables = () => {
    setSearchTerm('');
    cacheOperationHandler(CacheOperation.REMOVE_CACHE);
    refetchList();
  };

  const onClose = useCallback(
    () =>
      publishInternalEvents(
        TESTDATA_MODAL.CLOSE_ENVIRONMENT,
        isInsideRestApi ? { isInsideRestApi: true } : null,
      ),
    [publishInternalEvents],
  );
  const onDataClick = useCallback(
    (item) => {
      publishInternalEvents(TESTDATA_MODAL.SELECTED_ENVIRONMENT, {
        item,
        context,
      });
    },
    [publishInternalEvents],
  );
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch: refetchList,
  } = useInfiniteQuery(
    [`${CachingPrefix.FETCH_ENVIRONMENT_VARIABLES}s-${searchTerm || ''}`],
    async ({ pageParam = 0 }) =>
      await EnvironmentService.getVariables(
        projectId,
        pageParam,
        searchTerm,
        20,
      ),
    {
      getNextPageParam: (lastPage) =>
        lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1,
      staleTime: getCachingTime(),
      cacheTime: getCachingTime(),
    },
  );

  const modifiedData = data?.pages?.flatMap((item) => item?.content) || [];

  const [isCreateVariableLoading, setIsCreateVariableLoading] = useState(false);
  const createVariable = (newVariable, onSuccess) => {
    setIsCreateVariableLoading(true);
    const payload = createPayload([{ ...newVariable, projectId }]);
    EnvironmentService.updateVariables(projectId, payload)
      .then(() => {
        environmentHandler(CacheOperation.REMOVE_CACHE);
        toastify(
          t('common.message.created.success', { fieldName: 'Variable' }),
          {
            type: 'success',
          },
        );
        setSearchTerm('');
        onSuccess();
        refetchVariables();
      })
      .catch((err) => {
        const name = t('variable.label');
        const errorMessage = t('common.created.failure', { name });
        handleApiErrorMessages(err, t, errorMessage, name);
      })
      .finally(() => setIsCreateVariableLoading(false));
  };

  const testDevelopmentContext = useContext(TestDevelopmentContext);
  const versionContext = useContext(VersionContext);
  const version = testDevelopmentContext?.version || versionContext?.version;

  const openEnvironment = async () => {
    const baseURL = await getBaseUrl();
    window.open(`${baseURL}/ui/td/${version.id}/environments`, '_blank');
  };

  return (
    <TestDataDrawerLayout
      title={t('modal.title.environment')}
      symbol={TEST_DATA_SYMBOLS.ENVIRONMENT}
      onClose={onClose}
      helpInfo={helpInfoProps}
      className='!h-full'
    >
      <div className={classNames('px-5 py-6 h-full', animationClass)}>
        <EnvironmentValueSelector
          variables={modifiedData || []}
          onClick={onDataClick}
          isFetching={isFetching}
          setSearchTerm={setSearchTerm}
          hasMore={hasNextPage}
          handleLoadMore={fetchNextPage}
          createVariable={createVariable}
          searchTerm={searchTerm}
          openEnvironment={openEnvironment}
          refetch={refetchVariables}
          isCreateVariableLoading={isCreateVariableLoading}
        />
      </div>
    </TestDataDrawerLayout>
  );
};
